/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { Table } from "@tanstack/react-table";
import React, { useCallback, useEffect, useState } from "react";
import { TransactionDetail } from "types/history";
import { Filter, FiltersInterface } from "../../config/consts";
import { getAddress, Hash, isAddress } from "viem";
import { MAP_FILTER_TO_COL_ID } from "../../hooks/useActivityColumnDefinitions";
import SearchClear from "../../img/trade-history/search_clear.svg?react";
import Spyglass from "../../img/trade-history/spyglass.svg?react";
import { css } from "@emotion/react";
import { noop } from "lodash-es";
import { useIsTradeHistoryReturned } from "../../hooks/useIsTradeHistoryReturned";
import { SkeletonBlock } from "../general/Skeleton";
import { isAddressEqual } from "../../utils/tokenUtils";
import { ActivityStore } from "../../store/activity/ActivityStore";
import { GlobalStore } from "../../store/global/GlobalStore";

export interface TokenFilterProp {
  table: Table<TransactionDetail>;
  confirmedOptions: FiltersInterface[Filter.Token];
  setConfirmedOptions: (updatedOptions: FiltersInterface[Filter.Token]) => void;
}

export const TokenFilter: React.FC<TokenFilterProp> = ({
  table,
  confirmedOptions,
  setConfirmedOptions,
}) => {
  const [tokenSearchString, setTokenSearchString] = useState("");
  const isTradeHistoryReturned = useIsTradeHistoryReturned();
  const chainId = GlobalStore.useState(s => s.network?.id);
  const userTradedTokens = ActivityStore.useState(s => s.historyMetadata[chainId]);
  useEffect(() => {
    if (!confirmedOptions) {
      setTokenSearchString("");
    }
  }, [confirmedOptions]);

  const filterTokens = useCallback(
    (predicate: string) => {
      let isSearchingByTokenAddress = false;
      try {
        isSearchingByTokenAddress = isAddress(getAddress(predicate));
      } catch (error) {
        noop();
      }
      if (userTradedTokens != null) {
        // Check if the user is searching by token address
        if (isSearchingByTokenAddress) {
          const filteredResults = Object.keys(userTradedTokens).filter(tokenAddress => {
            return isAddressEqual(tokenAddress as Hash, getAddress(predicate));
          });
          return filteredResults;
        } else {
          const predicateLowercase = predicate.toLowerCase();
          const result = Object.entries(userTradedTokens)
            .filter(([key, val]) => {
              return (
                val?.symbol.toLowerCase().includes(predicateLowercase) ||
                val?.name.toLowerCase().includes(predicateLowercase)
              );
            })
            .map(([key, value]) => {
              return key;
            });
          return result;
        }
      }
      return undefined;
    },
    [userTradedTokens]
  );

  const handleSearch = useCallback(() => {
    if (tokenSearchString.length === 0) {
      setConfirmedOptions(undefined);
      return;
    }
    const filteredTokens = filterTokens(tokenSearchString);
    const colId = MAP_FILTER_TO_COL_ID[Filter.Token];
    const column = table.getColumn(colId);
    column?.setFilterValue((filteredTokens?.length ?? 0) > 0 ? filteredTokens : []);
    setConfirmedOptions(filteredTokens as unknown as FiltersInterface[Filter.Token]);
  }, [filterTokens, setConfirmedOptions, table, tokenSearchString]);

  const clearSearch = useCallback(() => {
    setTokenSearchString("");
    setConfirmedOptions(undefined);
    const colId = MAP_FILTER_TO_COL_ID[Filter.Token];
    const column = table.getColumn(colId);
    column?.setFilterValue(undefined);
  }, [setConfirmedOptions, table]);

  if (!isTradeHistoryReturned) {
    return (
      <SkeletonBlock
        isVisible={!isTradeHistoryReturned}
        width={"150px"}
        height={"30px"}
        borderRadius={"27px"}
      />
    );
  }

  return (
    <TokenFilterWrapper className={"token-filter"}>
      <Spyglass
        css={css`
          position: relative;
          margin-left: -26px; //Offset the relative positioning so it doesn't take up space
          left: 26px;
          top: 6px;
        `}
      />
      <TokenSearchInput
        placeholder={`Search tokens...`}
        onFocus={e => {
          e.target.select();
        }}
        value={tokenSearchString}
        onChange={e => {
          if (e.target.value.length > 0 && e.target.value.length < 300) {
            setTokenSearchString(e.target.value);
          } else {
            clearSearch();
          }
        }}
        onKeyDown={keyEvent => {
          if (keyEvent.key === "Enter") {
            handleSearch();
          }
        }}
        onBlur={() => {
          handleSearch();
        }}
      />
      <SearchClear
        css={css`
          display: ${tokenSearchString.length > 0 ? "inline-block" : "none"};
          position: relative;
          right: 26px;
          margin-right: -26px;
          top: 6px;
        `}
        onClick={() => clearSearch()}
      />
    </TokenFilterWrapper>
  );
};

const TokenFilterWrapper = styled.div`
  margin-top: 10px;
  @media (min-width: 769px) {
    margin-top: 0;
    margin-left: 10px;
  }
  display: flex;
  margin-left: 6px;
  padding-left: 2px;
  padding-right: 2px;
`;

// Sub-components
const TokenSearchInput = styled.input`
  width: 100%;
  height: 30px;
  background-color: black;
  color: white;
  font-size: 14px;
  border-radius: 27px;
  border: 0;
  padding-left: 34px;
  padding-right: 48px;

  &:focus {
    outline: 2px solid #01b04c;
  }
`;
